import React, { useEffect, useState } from "react";
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  updateDoc,
  getDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../utils/useAuth";
import { Link } from "react-router-dom";
import { createManagerNotification, createRefereeNotification } from "../utils/notifications";

async function cancelGame(id, userProfile) {
  if (!window.confirm("Are you sure you want to cancel this game?")) return;

  // Get game details before cancelling
  const gameSnap = await getDoc(doc(db, "games", id));
  const game = gameSnap.data();

  // Notify all assigned referees about the cancellation
  const refereeIds = [
    game.assignedRefereeId,
    game.assistantReferee1Id,
    game.assistantReferee2Id,
    game.assistant4Id,
    game.assistant5Id,
    game.timeKeeperId,
    game.playerMovementRecorderId,
    game.statisticianId,
    game.technicalZoneManagerId
  ].filter(Boolean);

  for (const refId of refereeIds) {
    await createRefereeNotification(
      refId,
      "Game Cancelled",
      `The game ${game.teamA} vs ${game.teamB} on ${game.date} at ${game.time} has been cancelled by ${userProfile.fullName}.`
    );
  }

  await updateDoc(doc(db, "games", id), {
    status: "cancelled",
  });

  // Send notification to managers
  await createManagerNotification(
    "Game Cancelled",
    `${userProfile.fullName} cancelled a game: ${game.teamA} vs ${game.teamB} on ${game.date} at ${game.time}.`,
    userProfile.union
  );

  alert("Game cancelled.");
}

export default function CoachDashboard() {
  const { user, profile } = useAuth();
  const [games, setGames] = useState([]);

  // FILTERS
  const [statusFilter, setStatusFilter] = useState("all");
  const [ageFilter, setAgeFilter] = useState("all");
  const [compFilter, setCompFilter] = useState("all");

  // DATE RANGE FILTER
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // SORT (if needed later)
  const [sortBy] = useState("date-desc");

  useEffect(() => {
    if (!user) return;

    const q = query(collection(db, "games"), where("coachId", "==", user.uid));

    const unsub = onSnapshot(q, (snap) => {
      setGames(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });

    return unsub;
  }, [user]);

  // FILTER FUNCTION
  function applyFilters(list) {
    return list
      .filter((g) =>
        statusFilter === "all" ? true : g.status === statusFilter
      )
      .filter((g) =>
        ageFilter === "all" ? true : g.ageGroup === ageFilter
      )
      .filter((g) =>
        compFilter === "all" ? true : g.competition === compFilter
      )
      .filter((g) =>
        startDate ? g.date >= startDate : true
      )
      .filter((g) =>
        endDate ? g.date <= endDate : true
      );
  }

  const filtered = applyFilters(games);

  // Split games by status
  const pendingGames = filtered.filter(g => g.status === 'pending' || !g.status);
  const assignedGames = filtered.filter(g => g.status === 'assigned');
  const scheduledGames = filtered.filter(g => g.status === 'scheduled');
  const completedGames = filtered.filter(g => g.status === 'completed' || g.status === 'cancelled');

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6">
      {/* HEADER */}
      <h2 className="text-xl md:text-2xl font-bold mb-4 md:mb-6">My Game Requests</h2>

      {/* FILTER + DATE RANGE BAR */}
      <div className="bg-white p-3 md:p-4 rounded shadow mb-4 md:mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-2 md:gap-4">

        {/* STATUS FILTER */}
        <select
          className="p-2 border rounded text-sm w-full"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="assigned">Assigned</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>

        {/* AGE FILTER */}
        <select
          className="p-2 border rounded text-sm w-full"
          value={ageFilter}
          onChange={(e) => setAgeFilter(e.target.value)}
        >
          <option value="all">All Age Groups</option>
          {[...new Set(games.map((g) => g.ageGroup))].map((age) => (
            <option key={age} value={age}>{age}</option>
          ))}
        </select>

        {/* COMPETITION FILTER */}
        <select
          className="p-2 border rounded text-sm w-full"
          value={compFilter}
          onChange={(e) => setCompFilter(e.target.value)}
        >
          <option value="all">All Competitions</option>
          {[...new Set(games.map((g) => g.competition))].map((comp) => (
            <option key={comp} value={comp}>{comp}</option>
          ))}
        </select>

        {/* DATE RANGE */}
        <div className="flex items-center space-x-2 col-span-1 sm:col-span-2">
          <label className="text-xs md:text-sm text-gray-600 whitespace-nowrap">From:</label>
          <input
            type="date"
            className="p-2 border rounded text-sm flex-1"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />

          <label className="text-xs md:text-sm text-gray-600 whitespace-nowrap">To:</label>
          <input
            type="date"
            className="p-2 border rounded text-sm flex-1"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
      </div>
      </div>

      {/* FOUR COLUMN LAYOUT - Responsive */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-3 md:gap-4">
        {/* COLUMN 1: PENDING GAMES */}
        <div>
          <div className="bg-yellow-600 text-white p-3 rounded-t-lg mb-3 flex items-center justify-between">
            <h3 className="text-lg font-bold">⏳ Pending</h3>
            <span className="bg-white text-yellow-600 px-2 py-1 rounded-full font-bold text-xs">
              {pendingGames.length}
            </span>
          </div>
          {pendingGames.length === 0 ? (
            <p className="text-gray-500 text-sm">No pending games.</p>
          ) : (
            <div className="space-y-3">
              {pendingGames.map((g) => (
                <div
                  key={g.id}
                  className="bg-white p-3 rounded shadow border-l-4 border-yellow-500"
                >
                  <h3 className="text-base font-semibold">
                    {g.teamA} vs {g.teamB}
                  </h3>
                  <p className="text-xs text-gray-600">
                    {g.date} — {g.time}
                  </p>
                  <p className="text-xs text-gray-600">
                    {g.venue} (Field: {g.field})
                  </p>
                  <p className="text-xs text-gray-600 mt-1">
                    {g.ageGroup} • {g.competition}
                  </p>

                  <p className="mt-2 px-2 py-1 inline-block bg-gray-500 text-white text-xs rounded">
                    Awaiting Assignment
                  </p>

                  <div className="flex flex-col gap-1 mt-3">
                    <Link
                      to={`/edit-game/${g.id}`}
                      className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 text-center"
                    >
                      Edit
                    </Link>
                    <button
                      onClick={() => cancelGame(g.id, profile)}
                      className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                    >
                      Cancel
                    </button>
                    <Link
                      to={`/game/${g.id}`}
                      className="px-2 py-1 text-xs bg-gray-700 text-white rounded hover:bg-gray-800 text-center"
                    >
                      Details
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* COLUMN 2: ASSIGNED GAMES */}
        <div>
          <div className="bg-blue-600 text-white p-3 rounded-t-lg mb-3 flex items-center justify-between">
            <h3 className="text-lg font-bold">📋 Assigned</h3>
            <span className="bg-white text-blue-600 px-2 py-1 rounded-full font-bold text-xs">
              {assignedGames.length}
            </span>
          </div>
          {assignedGames.length === 0 ? (
            <p className="text-gray-500 text-sm">No assigned games.</p>
          ) : (
            <div className="space-y-3">
              {assignedGames.map((g) => (
                <div
                  key={g.id}
                  className="bg-white p-3 rounded shadow border-l-4 border-blue-600"
                >
                  <h3 className="text-base font-semibold">
                    {g.teamA} vs {g.teamB}
                  </h3>
                  <p className="text-xs text-gray-600">
                    {g.date} — {g.time}
                  </p>
                  <p className="text-xs text-gray-600">
                    {g.venue} (Field: {g.field})
                  </p>
                  <p className="text-xs text-gray-600 mt-1">
                    {g.ageGroup} • {g.competition}
                  </p>

                  {/* Referee Info */}
                  {g.assignedRefereeName && (
                    <div className="mt-2 px-2 py-1 bg-blue-50 border border-blue-600 rounded">
                      <p className="text-xs font-semibold text-blue-700">Assigned Referee</p>
                      <p className="text-xs font-semibold">{g.assignedRefereeName}</p>
                      {g.assignedRefereeLeague && (
                        <p className="text-xs text-gray-600">{g.assignedRefereeLeague}</p>
                      )}
                      <p className="text-xs mt-1 text-yellow-600">Awaiting Acceptance</p>
                    </div>
                  )}

                  <div className="flex flex-col gap-1 mt-3">
                    <Link
                      to={`/edit-game/${g.id}`}
                      className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 text-center"
                    >
                      Edit
                    </Link>
                    <button
                      onClick={() => cancelGame(g.id, profile)}
                      className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                    >
                      Cancel
                    </button>
                    <Link
                      to={`/game/${g.id}`}
                      className="px-2 py-1 text-xs bg-gray-700 text-white rounded hover:bg-gray-800 text-center"
                    >
                      Details
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* COLUMN 3: SCHEDULED GAMES */}
        <div>
          <div className="bg-green-600 text-white p-3 rounded-t-lg mb-3 flex items-center justify-between">
            <h3 className="text-lg font-bold">✓ Scheduled</h3>
            <span className="bg-white text-green-600 px-2 py-1 rounded-full font-bold text-xs">
              {scheduledGames.length}
            </span>
          </div>
          {scheduledGames.length === 0 ? (
            <p className="text-gray-500 text-sm">No scheduled games.</p>
          ) : (
            <div className="space-y-3">
              {scheduledGames.map((g) => (
                <div
                  key={g.id}
                  className="bg-white p-3 rounded shadow border-l-4 border-green-600"
                >
                  <h3 className="text-base font-semibold">
                    {g.teamA} vs {g.teamB}
                  </h3>
                  <p className="text-xs text-gray-600">
                    {g.date} — {g.time}
                  </p>
                  <p className="text-xs text-gray-600">
                    {g.venue} (Field: {g.field})
                  </p>
                  <p className="text-xs text-gray-600 mt-1">
                    {g.ageGroup} • {g.competition}
                  </p>

                  {/* Referee Assignment Info */}
                  {g.assignedRefereeName && (
                    <div className="mt-2 space-y-1">
                      <div className="px-2 py-1 bg-green-50 border border-green-600 rounded">
                        <p className="text-xs font-semibold text-green-700">Main Referee</p>
                        <p className="text-xs font-semibold">{g.assignedRefereeName}</p>
                        {g.assignedRefereeLeague && (
                          <p className="text-xs text-gray-600">{g.assignedRefereeLeague}</p>
                        )}
                        {g.refereeStatus && (
                          <p className="text-xs mt-1">
                            Status: <span className={`font-semibold ${g.refereeStatus === 'accepted' ? 'text-green-600' : 'text-yellow-600'}`}>
                              {g.refereeStatus.toUpperCase()}
                            </span>
                          </p>
                        )}
                      </div>
                      
                      {/* Show assistant referees if assigned */}
                      {g.assistantReferee1Name && (
                        <p className="text-xs text-gray-700">AR1: {g.assistantReferee1Name}</p>
                      )}
                      {g.assistantReferee2Name && (
                        <p className="text-xs text-gray-700">AR2: {g.assistantReferee2Name}</p>
                      )}
                    </div>
                  )}

                  <div className="flex flex-col gap-1 mt-3">
                    <Link
                      to={`/edit-game/${g.id}`}
                      className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 text-center"
                    >
                      Edit
                    </Link>
                    <button
                      onClick={() => cancelGame(g.id, profile)}
                      className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                    >
                      Cancel
                    </button>
                    <Link
                      to={`/game/${g.id}`}
                      className="px-2 py-1 text-xs bg-gray-700 text-white rounded hover:bg-gray-800 text-center"
                    >
                      Details
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* COLUMN 4: COMPLETED GAMES */}
        <div>
          <div className="bg-gray-700 text-white p-3 rounded-t-lg mb-3 flex items-center justify-between">
            <h3 className="text-lg font-bold">📁 Completed</h3>
            <span className="bg-white text-gray-700 px-2 py-1 rounded-full font-bold text-xs">
              {completedGames.length}
            </span>
          </div>
          {completedGames.length === 0 ? (
            <p className="text-gray-500 text-sm">No completed games.</p>
          ) : (
            <div className="space-y-3">
              {completedGames.map((g) => (
                <div
                  key={g.id}
                  className={`bg-white p-3 rounded shadow border-l-4 ${
                    g.status === 'cancelled' ? 'border-red-500' : 'border-gray-500'
                  }`}
                >
                  <h3 className="text-base font-semibold">
                    {g.teamA} vs {g.teamB}
                  </h3>
                  <p className="text-xs text-gray-600">
                    {g.date} — {g.time}
                  </p>
                  <p className="text-xs text-gray-600">
                    {g.venue} (Field: {g.field})
                  </p>
                  <p className="text-xs text-gray-600 mt-1">
                    {g.ageGroup} • {g.competition}
                  </p>

                  {g.finalScore && (
                    <div className="mt-2 p-2 bg-gray-100 rounded">
                      <p className="text-sm font-bold text-center">
                        {g.finalScore}
                      </p>
                    </div>
                  )}

                  <p className="mt-2 text-xs">
                    <span className={`font-semibold ${
                      g.status === 'cancelled' ? 'text-red-600' : 'text-gray-700'
                    }`}>
                      {g.status?.toUpperCase()}
                    </span>
                  </p>

                  <div className="flex flex-col gap-1 mt-3">
                    <Link
                      to={`/game/${g.id}`}
                      className="px-2 py-1 text-xs bg-gray-700 text-white rounded hover:bg-gray-800 text-center"
                    >
                      View Details
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
